import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { LeaveAdjustmentService } from '../leave-adjustment.service';
import * as LeaveAdjustmentActions from './leave-adjustment.actions';
import * as CurrentLeaveSummaryActions from './current-leave-summary.actions';
import * as fromErrors from '../../shared';
import {
    ResponseStatus,
    IStatusMessage,
    DisplayMessageMinimum
} from '../../shared';
import * as fromShared from '../../shared';
import { catchError, concatMap, flatMap, switchMap } from 'rxjs/operators';
import { LeaveAdjustment } from '../ILeaveAdjustment';

@Injectable()
export class LeaveAdjustmentEffects {
    constructor(
        private actions$: Actions,
        private service: LeaveAdjustmentService
    ) {}

    getAdjustments$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LeaveAdjustmentActions.getAdjustments),
            switchMap(a =>
                this.service.getAdjustments(a.PPS, a.PPE).pipe(
                    flatMap(resp => [
                        LeaveAdjustmentActions.loadLeaveAdjustments({
                            leaveAdjustments: resp
                        }),
                        new fromShared.Errors.RemoveErrors(
                            resp
                                .filter(
                                    x =>
                                        x.Status.StatusCode ===
                                        ResponseStatus.Success
                                )
                                .map(x => x.Uniqueid)
                        ),
                        new fromShared.Errors.SetErrors(
                            [
                                ...resp
                                    .filter(
                                        a =>
                                            a.Status.StatusCode >=
                                            DisplayMessageMinimum
                                    )
                                    .map(a => a.Status)
                            ].filter(fromShared.Errors.filterErrors)
                        )
                    ]),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        );
    });

    putRecalcAdjustments$ = createEffect(() =>
        this.actions$.pipe(
            ofType(LeaveAdjustmentActions.putRecalcAdjustments),
            switchMap(a =>
                this.service.putAdjustments(a.adjustments).pipe(
                    flatMap(c => [
                        LeaveAdjustmentActions.upsertLeaveAdjustments({
                            leaveAdjustments: c.Items
                        }),
                        new fromShared.Errors.RemoveErrors(
                            c.Items.filter(
                                x =>
                                    x.Status.StatusCode ===
                                    ResponseStatus.Success
                            ).map(x => x.Uniqueid.toString())
                        ),
                        new fromShared.Errors.SetErrors(
                            [
                                ...c.ErrorMessages.map(
                                    x =>
                                        ({
                                            StatusCode: -1,
                                            Message: x,
                                            MsgProcessID: 0,
                                            MsgEntityID: ''
                                        } as IStatusMessage)
                                ),
                                ...c.Items.filter(
                                    x =>
                                        x &&
                                        x.Status.StatusCode >=
                                            DisplayMessageMinimum
                                ).map(x => x.Status)
                            ].filter(fromShared.Errors.filterErrors)
                        )
                    ]),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        )
    );

    putAdjustments$ = createEffect(() =>
        this.actions$.pipe(
            ofType(LeaveAdjustmentActions.putAdjustments),
            switchMap(a =>
                this.service.putAdjustments(a.adjustments).pipe(
                    flatMap(c => [
                        LeaveAdjustmentActions.updateLeaveAdjustments({
                            leaveAdjustments: [
                                {
                                    id: a.adjustments
                                        .map(x => x.Uniqueid)
                                        .toString(),
                                    changes: c.Items[0]
                                }
                            ]
                        }),
                        new fromShared.Errors.RemoveErrors(
                            c.Items.filter(
                                x =>
                                    x.Status.StatusCode ===
                                    ResponseStatus.Success
                            ).map(x => x.Uniqueid.toString())
                        ),
                        new fromShared.Errors.SetErrors(
                            [
                                ...c.ErrorMessages.map(
                                    x =>
                                        ({
                                            StatusCode: -1,
                                            Message: x,
                                            MsgProcessID: 0,
                                            MsgEntityID: ''
                                        } as IStatusMessage)
                                ),
                                ...c.Items.filter(
                                    x =>
                                        x &&
                                        x.Status.StatusCode >=
                                            DisplayMessageMinimum
                                ).map(x => x.Status)
                            ].filter(fromShared.Errors.filterErrors)
                        ),
                        CurrentLeaveSummaryActions.getSummary({employeeListID: a.adjustments[0].EmployeeListID})
                    ]),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        )
    );
}
